import { useCallback, useState } from 'react';
import {
  useDirectDebitIcbcAnalysis,
  useDirectDebitIcbcPreview,
  useExportDirectDebitIcbc,
} from '../../../hooks/useCoupons';
import { ICBC_DEFAULTS } from '../constants/directDebitIcbcConstants';
import {
  cleanDirectDebitIcbcFilters,
  getDirectDebitIcbcErrorMessage,
  isPastDateInputValue,
  toDateInputValue,
} from '../utils/directDebitIcbcUtils';

const now = new Date();

const INITIAL_FILTER = {
  month: null,
  year: null,
  due_date: toDateInputValue(now),
  cuit: ICBC_DEFAULTS.cuit,
  currency: ICBC_DEFAULTS.currency,
  prestation: ICBC_DEFAULTS.prestation,
};

export const useDirectDebitIcbc = () => {
  const [filter, setFilter] = useState(INITIAL_FILTER);
  const [preview, setPreview] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const previewMutation = useDirectDebitIcbcPreview();
  const exportMutation = useExportDirectDebitIcbc();
  const analysisQuery = useDirectDebitIcbcAnalysis();

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleFilter = useCallback((event) => {
    const { name, value } = event.target;
    setFilter((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'due_date' ? { month: null, year: null } : {}),
    }));
    setPreview(null);
  }, []);

  const handleSelectAnalysisDate = useCallback((analysisItem) => {
    setFilter((prev) => ({
      ...prev,
      due_date: analysisItem.due_date,
      month: analysisItem.period_month,
      year: analysisItem.period_year,
    }));
    setPreview(null);
  }, []);

  const validateFilters = useCallback((cleanFilters) => {
    if (!cleanFilters.due_date) {
      return 'Debe seleccionar un vencimiento.';
    }

    if (isPastDateInputValue(cleanFilters.due_date)) {
      return 'No se pueden seleccionar fechas vencidas.';
    }

    if (!/^\d{11}$/.test(cleanFilters.cuit)) {
      return 'El CUIT debe tener 11 dígitos.';
    }

    if (!cleanFilters.prestation || cleanFilters.prestation.length > 10) {
      return 'La prestación debe tener entre 1 y 10 caracteres.';
    }

    if (!/^\d$/.test(cleanFilters.currency)) {
      return 'La moneda debe tener 1 dígito.';
    }

    return null;
  }, []);

  const handleSearch = useCallback(async () => {
    const cleanFilters = cleanDirectDebitIcbcFilters(filter);
    const validationError = validateFilters(cleanFilters);

    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }

    try {
      const response = await previewMutation.mutateAsync(cleanFilters);
      setPreview(response);
      showSnackbar('Consulta de débito directo ICBC generada correctamente.');
    } catch (error) {
      showSnackbar(
        getDirectDebitIcbcErrorMessage(error, 'No se pudo consultar débito directo ICBC.'),
        'error'
      );
    }
  }, [filter, previewMutation, showSnackbar, validateFilters]);

  const handleExport = useCallback(async () => {
    const cleanFilters = cleanDirectDebitIcbcFilters(filter);
    const validationError = validateFilters(cleanFilters);

    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }

    try {
      const result = await exportMutation.mutateAsync(cleanFilters);
      showSnackbar(`Archivos ${result.filenames.join(' y ')} generados correctamente.`);
    } catch (error) {
      showSnackbar(
        getDirectDebitIcbcErrorMessage(error, 'No se pudo exportar el archivo ICBC.'),
        'error'
      );
    }
  }, [exportMutation, filter, showSnackbar, validateFilters]);

  return {
    filter,
    preview,
    analysis: analysisQuery.data || null,
    records: preview?.records || [],
    excluded: preview?.excluded || [],
    summary: preview?.summary || null,
    snackbar,
    isLoading: previewMutation.isPending,
    isExporting: exportMutation.isPending,
    isAnalysisLoading: analysisQuery.isLoading,
    handleFilter,
    handleSelectAnalysisDate,
    handleSearch,
    handleExport,
    handleCloseSnackbar,
  };
};
